import type { OfflineLedgerEntry } from '@/lib/offline/offline-queue'
import { isTauri } from '@/lib/utils/tauri'

import { SessionChangedError } from '@scrambl/shared/session-scope'

import type { CacheOwner } from './cache-scope'
import type { DisposablePersister } from './throttled-persister'
import {
  adoptLegacyOfflineSnapshot,
  CACHE_PREFIX,
  cacheOwnerPrefix,
} from './cache-scope'
import { createIndexedDBPersister } from './idb-persister'
import { captureNativeCacheSession } from './native-cache-session'
import { createSQLitePersister } from './sqlite-persister'
import { createSerializedThrottledPersister } from './throttled-persister'

const PERSIST_THROTTLE_MS = 1500

export async function createPersister(
  scopeKey: string,
  legacyEntries: () => readonly OfflineLedgerEntry[] = () => [],
): Promise<DisposablePersister | null> {
  // Change the schema only when the persisted query shape becomes incompatible.
  // A deployment timestamp must not invalidate otherwise reusable offline data.
  const cacheKey = `${CACHE_PREFIX}${scopeKey}-writer-${crypto.randomUUID()}-schema-1`
  const scopePrefix = `${CACHE_PREFIX}${scopeKey}-`

  try {
    const [userId, login, organizationId, relationshipId] = JSON.parse(
      scopeKey,
    ) as [string, string, string, string | null]
    const native = captureNativeCacheSession({ userId, login })
    await native?.verify()
    const legacyPrefix = `scrambl-query-cache-${JSON.stringify([userId, organizationId, relationshipId])}-`
    const create = isTauri() ? createSQLitePersister : createIndexedDBPersister
    const inner = await create(cacheKey, scopePrefix)
    const ownWriter = await create(cacheKey)
    const legacy = await create(legacyPrefix + 'schema-1', legacyPrefix)
    await native?.verify()
    return createSerializedThrottledPersister(
      {
        ...inner,
        persistClient: async client => {
          native?.assertCurrent()
          await inner.persistClient(client)
        },
        removeClient: ownWriter.removeClient,
        restoreClient: async () => {
          await native?.verify()
          const current = await inner.restoreClient()
          await native?.verify()
          if (current) return current
          const previous = await legacy.restoreClient()
          await native?.verify()
          return adoptLegacyOfflineSnapshot(
            previous,
            { userId, login },
            organizationId,
            legacyEntries(),
          )
        },
      },
      PERSIST_THROTTLE_MS,
    )
  } catch (error) {
    if (!(error instanceof SessionChangedError))
      console.warn(
        '[QueryCache] Failed to create persister, running without cache',
      )
    return null
  }
}

/** Teardown removes only the captured login's organizations, never a newer login. */
export async function removeOwnerCaches(owner: CacheOwner): Promise<void> {
  const prefix = cacheOwnerPrefix(owner)
  if (isTauri()) {
    const { removeSQLiteCaches } = await import('./sqlite-persister')
    await removeSQLiteCaches(prefix)
  } else {
    const { removeIndexedDBCaches } = await import('./idb-persister')
    await removeIndexedDBCaches(prefix)
  }
}

/** Permanent deletion is distinct from retiring one login's writer. */
export async function deleteUserCaches(userId: string): Promise<void> {
  if (isTauri()) {
    const { deleteSQLiteUserCaches } = await import('./sqlite-persister')
    await deleteSQLiteUserCaches(userId)
  }
  const { deleteIndexedDBUserCaches } = await import('./idb-persister')
  await deleteIndexedDBUserCaches(userId)
}

/** Device removal needs a unique, durable receipt ID and retired credentials. */
export async function forgetUserCaches(
  userId: string,
  operationId: string,
): Promise<void> {
  if (isTauri()) {
    const { forgetSQLiteUserCaches } = await import('./sqlite-persister')
    await forgetSQLiteUserCaches(userId, operationId)
  }
  const { forgetIndexedDBUserCaches } = await import('./idb-persister')
  await forgetIndexedDBUserCaches(userId, operationId)
}
